import * as React from 'react';
import { createContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDocumentByIdService } from '../../../services/DocumentService';
import { useTranslate } from 'sbx-crm-menu-ui';
import InteractiveTabsComponent, { InteractiveTab } from '../../shared/InteractiveTabsComponent';
import { Brain, Files, History } from 'lucide-react';
import { LoadingComponent } from '../../shared/LoadingComponent';
import DocumentSetupComponent from './DocumentSetupComponent';
import {
    Control,
    SubmitErrorHandler,
    SubmitHandler,
    useForm,
    UseFormGetValues,
    UseFormSetValue,
    UseFormWatch,
} from 'react-hook-form';
import { getAllAgentsServiceV2 } from '../../../services/AgentService';
import DocumentAnalysisComponent from './DocumentAnalysisComponent';
import { getFolderFilesService } from '../../../services/AssetService';
import { FileProps } from '../../shared/DragAndDrop';
import { Document } from 'src/types/Document';
import DocumentHistoryComponent from './DocumentHistoryComponent';
import NavbarBreadcrumbV2 from '../../shared/NavbarBreadcrumbV2';

type DocumentConfig = {
    vs_config: {
        chunk_size: number;
        chunk_overlap: number;
    };
    doc_analysis_params: {
        contract_analysis_revision: string;
        contract_analysis_classification: string[] | null;
        contract_analysis_examples: string[] | null;
        sentiment_analysis_threshold: number;
    };
};

export type DocumentAnalysis = DocumentConfig & {
    agent_id: number
    doc_id: number
    user_id: number
}

export type DocumentForm = {
    document: {
        id?: number
        name: string;
        source: string;
        config: DocumentConfig;
    };
    analysis: DocumentAnalysis
}

interface FormContextType {
    watch: UseFormWatch<DocumentForm>;
    setValue: UseFormSetValue<DocumentForm>;
    getValues: UseFormGetValues<DocumentForm>;
    control: Control<DocumentForm>;
    document?: Document;
    handleSubmit: (onValid: SubmitHandler<DocumentForm>, onInvalid?: SubmitErrorHandler<DocumentForm>) => (e?: React.BaseSyntheticEvent) => Promise<void>;
    agents: { value: number, label: string }[];
}

const defaultConfigValues = {
    vs_config: {
        chunk_size: 0,
        chunk_overlap: 0,
    },
    doc_analysis_params: {
        contract_analysis_revision: '',
        contract_analysis_classification: [],
        contract_analysis_examples: [],
        sentiment_analysis_threshold: 0,
    },
};

// Creamos el contexto con un valor inicial vacío (será sobrescrito luego)
export const DocumentSetupContext = createContext<FormContextType>({
    watch: () => ({}) as any,
    setValue: () => {
    },
    getValues: () => ({}) as any,
    control: {} as Control<DocumentForm>,
    agents: [],
    handleSubmit: () => async () => {
    },
});

const disabledTabs = ['document_analysis', 'history'];

const DocumentPageComponent = () => {
    const { id } = useParams();
    const { t: common } = useTranslate('common');
    const { t: document } = useTranslate('document');

    const [files, setFiles] = useState<File[]>([]);
    const [filesComponent, setFilesComponent] = useState<FileProps[]>([]);

    const { data, isLoading, refetch, isRefetching } = useQuery({
        queryKey: ['document', id],
        queryFn: async () => await getDocumentByIdService(id as string),
        enabled: !!id && id !== 'create',
    });

    const filesQuery = useQuery({
        queryKey: ['files'],
        queryFn: async () => await getFolderFilesService(data!.item.getSource()),
        enabled: !!data?.item?.getSource(),
    });


    const agentQuery = useQuery({
        queryKey: ['agents'],
        queryFn: async () => await getAllAgentsServiceV2().then(res => res.items.map(item => ({
            label: item.name,
            value: item.id ?? 1,
        }))),
    });

    React.useEffect(() => {

        if (data?.item?.id) {
            setValue('document', {
                name: data.item.name,
                source: data.item.getSource(),
                config: {
                    doc_analysis_params: data.item?.getConfig()?.doc_analysis_params,
                    vs_config: data.item.getConfig()?.vs_config,
                },
            });

            setValue('analysis', {
                doc_id: data.item.id,
                user_id: 0,
                agent_id: 0,
                doc_analysis_params: data.item?.getConfig()?.doc_analysis_params,
                vs_config: data.item.getConfig()?.vs_config,
            });
        }

    }, [data]);

    React.useEffect(() => {
        if (filesQuery.data?.items && filesQuery.data.items.length > 0) {
            setFilesComponent(filesQuery.data.items.map(item => ({
                key: item.key,
                name: item.name,
                type: item.mimetype,
                size: item.size,
            })));
        }

    }, [filesQuery.data]);

    const { control, watch, setValue, getValues, handleSubmit } = useForm<DocumentForm>({
        defaultValues: {
            document: {
                name: '',
                source: '',
                config: defaultConfigValues,
            },
            analysis: {
                ...defaultConfigValues,
                doc_id: 0,
                agent_id: 0,
                user_id: 0,
            },
        },
    });

    const tabs: InteractiveTab[] = useMemo(() => [
        {
            id: 'document',
            label: document('document'),
            color: '#E11D48',
            icon: <Files />,
            component: <DocumentSetupComponent
                getDocument={refetch}
                setFiles={setFiles} files={files}
                getFiles={filesQuery.refetch}
                filesComponent={filesComponent} setFilesComponent={setFilesComponent} />,
        },
        {
            id: 'document_analysis',
            label: document('document_analysis'),
            color: '#FF9F1C',
            icon: <Brain />,
            component: <DocumentAnalysisComponent />,
        },
        {
            id: 'history',
            label: common('history'),
            color: '#2EC4B6',
            icon: <History />,
            component: <DocumentHistoryComponent
                source={data?.item.getSource() || ''} />,
        },
    ], [filesComponent, files]);


    if (isLoading || isRefetching) {
        return <LoadingComponent />;
    }


    return (
        <div
            className="grid grid-cols-1 min-h-screen bg-gradient-to-br from-pink-100 to-indigo-100 p-8">
            <div className="container mx-auto p-4 space-y-4">
                <NavbarBreadcrumbV2 routes={[
                        {
                            label: document('document_list'),
                            route: '/document'
                        },
                        {
                            label: id === 'create' ? document('create_document') : data?.item.name || ''
                        }
                    ]} 
                />

                <div className="flex flex-col gap-2 bg-white rounded-lg shadow-lg p-6">
                    <DocumentSetupContext.Provider value={{
                        watch,
                        setValue,
                        getValues,
                        control,
                        handleSubmit,
                        document: data?.item ?? undefined,
                        agents: agentQuery?.data ?? [],
                    }}>
                        <InteractiveTabsComponent
                            tabs={tabs.filter(tab => id && id === 'create' ? !disabledTabs.includes(tab.id) : true)} />
                    </DocumentSetupContext.Provider>
                </div>
            </div>
        </div>
    );
};

export default DocumentPageComponent;